// QuickPhoto.jsx — the IPD ward quick photo-input: capture (or pick) one photo at
// the bed and attach it to THAT patient's record, so it appears in the bed
// workspace Reports tab.
//
// WRITE PATH — the logged-in clinician's own Medplum session. Staff policies
// grant Binary CREATE only plus the existing RLS-scoped DocumentReference arm;
// the browser never switches to a shared upload identity. The DOC is shaped to
// EXACTLY what shapeReportRow reads
// (subject + context.encounter + content[0].attachment{contentType,url,title}),
// so the photo lands in the bed's Reports tab; the doctor reads it back later
// through their OWN RLS-scoped session (listBedReports). Medplum rewrites the
// `Binary/<id>` attachment.url into a presigned URL on every authorized read.
//
// FAIL LOUD (AGENTS §1.3): an upload error is SHOWN and the capture is kept so it
// can be retried — nothing is silently lost. A missing/non-UUID patient id throws
// rather than writing an orphan photo no RLS-scoped search could surface.
import React from 'react';
import { createPortal } from 'react-dom';
import { Camera, X, RotateCcw, Check, Image as ImageIcon, Mic, Flashlight, SwitchCamera } from 'lucide-react';
import { getMedplum, authHeader } from '../../lib/medplum-client.js';
import { Button } from '@/components/ui/button.jsx';
import { apiUrl } from '@/lib/net-base.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => UUID_RE.test(String(v || ''));

// The same _healther-bag extension medplum-store.mjs / medplum-photo.js use, so
// photo context keeps ONE FHIR-side convention app-wide.
const EXT_OPD_HEALTHER = 'http://healther.io/fhir/StructureDefinition/opd-healther';

// ── CONTEXT LAYER (owner ask 2026-07-10): after capture the photo goes through
// UNDERSTANDING before attach. The honest reachable vision surface for a
// logged-in staff user is the kiosk-voice Cerebras leg —
// POST /api/v1/opd/kiosk-voice/cerebras/chat — whose gate (authorizeKioskVoice,
// server/opd/kiosk-voice.mjs) accepts a staff Medplum BEARER, so we call it via
// authHeader() like the other surviving Express routes. The model is pinned
// server-side (Gemma on Cerebras, multimodal image_url; the same leg the kiosk
// uses in lib/gemma.js). The OPD_VISION sidecar (server/opd/vision/routes.mjs)
// is deliberately NOT used: it is flag-gated default-off, module-scoped
// (anthropometry/eye/derm), and needs a separate sidecar deploy. When Cerebras
// is not configured (503 cerebras-not-configured) or Express is absent,
// understandPhoto FAILS HONESTLY with a readable reason the UI renders — the
// manual type chips + note still work, and attach is never blocked.
export const PHOTO_KINDS = [
  { key: 'wound', label: 'Wound' },
  { key: 'id-card', label: 'ID card' },
  // Front-desk document kinds (owner ask 2026-07-10): reception captures ID
  // cards, insurance cards, referral letters and old prescriptions. These live
  // in the ONE shared kind set (not a reception fork) so every surface's vision
  // read + chips speak the same vocabulary; readers never switch on the enum, so
  // the two new values are backward-compatible.
  { key: 'insurance', label: 'Insurance' },
  { key: 'referral', label: 'Referral' },
  { key: 'prescription', label: 'Prescription' },
  { key: 'report', label: 'Report' },
  { key: 'other', label: 'Other' },
];
const kindLabel = (key) => (PHOTO_KINDS.find((k) => k.key === key) || {}).label || 'Photo';

// ── CONTEXT-AWARE CONFIRMATION (owner ask 2026-07-10). A `context`
// ({ surface, role, patientName }) lets QuickPhoto pre-seed the LIKELY photo
// kind for the surface and speak a one-line sentence so the user confirms ONCE
// already understanding what will be written. surface ∈
// opd-consult|ipd-round|ward-board|admit|emergency|reception. The vision read still wins
// once it returns; the user's chip still wins over both.
export const SURFACE_DEFAULT_KIND = {
  admit: 'id-card',        // the classic admission ID-card capture
  'ipd-round': 'wound',    // bedside rounds → wound/site photos
  'ward-board': 'wound',
  'opd-consult': 'report',
  emergency: 'wound',
  reception: 'id-card',    // front desk → the classic ID / insurance / referral capture
};
const SURFACE_PHRASE = {
  admit: 'admission',
  'ipd-round': 'ward round',
  'ward-board': 'ward board',
  'opd-consult': 'consult',
  emergency: 'emergency',
  reception: 'check-in',
};
// One-line context sentence, e.g. "Added during the ward round by the nurse —
// attaching to Fatima Begum's record as a wound photo. Confirm?" Degrades
// gracefully when a piece is missing.
export function contextSentence({ surface, role, name, kind }) {
  const where = SURFACE_PHRASE[surface];
  const who = String(role || '').trim();
  const lead = where
    ? `Added during the ${where}${who ? ` by the ${who}` : ''}`
    : (who ? `Added by the ${who}` : 'Adding this photo');
  const target = name ? `${name}'s record` : 'this patient’s record';
  const label = kindLabel(kind).toLowerCase();
  const article = /^[aeiou]/.test(label) ? 'an' : 'a';
  return `${lead} — attaching to ${target} as ${article} ${label} photo. Confirm?`;
}

const UNDERSTAND_PROMPT =
  'You are a clinical intake assistant. Look at this photo and answer with STRICT JSON only, no prose:\n' +
  '{"kind":"wound|id-card|insurance|referral|prescription|report|other","summary":"<one short line: what this is>","fields":{}}\n' +
  'fields: only what is CLEARLY readable — id-card: {"name","age","idNumber"}; ' +
  'insurance card: {"insurer","policyNumber","memberName"}; referral letter: {"referredBy","referredTo","reason"}; ' +
  'paper prescription: {"meds":["..."]}; ' +
  'lab report: {"testName","keyValues"}. Never guess or invent a value; omit anything unclear. Unreadable image → kind "other".';

// understandPhoto(dataUrl) → { kind, summary, fields, source:'vision' }; THROWS
// a human-readable reason when vision is not reachable (the caller renders it).
export async function understandPhoto(dataUrl) {
  const auth = authHeader();
  if (!auth.Authorization) throw new Error('vision needs a signed-in staff session');
  const r = await fetch(apiUrl('/api/v1/opd/kiosk-voice/cerebras/chat'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...auth },
    body: JSON.stringify({
      messages: [{
        role: 'user',
        content: [
          { type: 'text', text: UNDERSTAND_PROMPT },
          { type: 'image_url', image_url: { url: dataUrl } },
        ],
      }],
    }),
  }).catch(() => null);
  if (!r) throw new Error('vision unavailable — the app server is not reachable');
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    throw new Error(j.error === 'cerebras-not-configured'
      ? 'vision unavailable — CEREBRAS_API_KEY is not set on this server'
      : `vision unavailable — ${j.error || `HTTP ${r.status}`}`);
  }
  const raw = j.content || j.choices?.[0]?.message?.content || '';
  const m = String(raw).match(/\{[\s\S]*\}/);
  if (!m) throw new Error('vision returned no readable result');
  let parsed;
  try { parsed = JSON.parse(m[0]); } catch (_) { throw new Error('vision returned no readable result'); }
  const kind = PHOTO_KINDS.some((k) => k.key === parsed.kind) ? parsed.kind : 'other';
  return {
    kind,
    summary: String(parsed.summary || '').slice(0, 200),
    fields: (parsed.fields && typeof parsed.fields === 'object' && !Array.isArray(parsed.fields)) ? parsed.fields : {},
    source: 'vision',
  };
}

// Flatten extracted fields to short display lines ("name: Sunita Devi").
export function fieldLines(fields) {
  const out = [];
  for (const [k, v] of Object.entries(fields || {})) {
    if (v == null || v === '') continue;
    out.push(`${k}: ${Array.isArray(v) ? v.join(', ') : (typeof v === 'object' ? JSON.stringify(v) : String(v))}`);
    if (out.length >= 6) break;
  }
  return out;
}

// Remembered lens across QuickPhoto opens WITHIN a session (owner ask 2026-07-13:
// "remember the last choice per session"). Module-scope so every capture surface
// that mounts QuickPhoto reopens on the lens the user last chose; resets on reload.
let LAST_FACING = 'environment';

const MAX_DIM = 1280; // longest edge — same cap as the kiosk clinical-photo path

// Downscale a source (video frame or <img>) to a ≤1280px JPEG Blob (quality .9),
// the kiosk pattern. Returns { blob, dataUrl } (dataUrl for the preview).
function toJpeg(source, srcW, srcH) {
  const scale = Math.min(1, MAX_DIM / Math.max(srcW || 1, srcH || 1));
  const w = Math.max(1, Math.round((srcW || 1) * scale));
  const h = Math.max(1, Math.round((srcH || 1) * scale));
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  cv.getContext('2d').drawImage(source, 0, 0, w, h);
  const dataUrl = cv.toDataURL('image/jpeg', 0.9);
  return new Promise((resolve, reject) => {
    cv.toBlob((b) => (b ? resolve({ blob: b, dataUrl }) : reject(new Error('could not encode the photo'))), 'image/jpeg', 0.9);
  });
}

// Read an image File → a ≤1280px JPEG File (+ dataUrl preview). Used by the
// command-agent paperclip so a staged photo is capped before it is attached.
export function resizeImageFile(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = async () => {
      try {
        const { blob, dataUrl } = await toJpeg(img, img.naturalWidth, img.naturalHeight);
        URL.revokeObjectURL(img.src);
        resolve({ file: new File([blob], 'photo.jpg', { type: 'image/jpeg' }), dataUrl });
      } catch (e) { reject(e); }
    };
    img.onerror = () => reject(new Error('Could not read that image.'));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * attachWardPhoto — the small, self-contained write helper (also reused by the
 * command agent's attach_photo executor). Binary first, then the DocumentReference
 * pointing at it, both as the logged-in user.
 *
 *   attachWardPhoto({ file, patientId (UUID), encounterId? (UUID), note?, title?,
 *                     understanding? ({ kind, summary, fields, source }),
 *                     context? ({ surface, role, patientName }) })
 *     → { ok, binaryId, documentReferenceId }
 *
 * `context` is the CAPTURE SURFACE (where/who) — surface is one of
 * opd-consult|ipd-round|ward-board|admit|emergency|reception, role is the capturing
 * staff's role. It rides in the same _healther-bag extension as everything else
 * so a later reader can say "attached during the ward round by the nurse".
 */
export async function attachWardPhoto({ file, patientId, encounterId, note, title, contentType, understanding, context }) {
  if (!file) throw new Error('attachWardPhoto: a photo file is required');
  if (!isUuid(patientId)) {
    throw new Error(`attachWardPhoto: patientId must be the Medplum Patient UUID (got "${patientId}")`);
  }
  const { medplum } = await getMedplum();
  const type = contentType || file.type || 'image/jpeg';
  const cleanNote = (note || '').trim();
  const u = understanding || null;
  const label = cleanNote || (u && u.summary) || (u && u.kind && u.kind !== 'other' && `${kindLabel(u.kind)} photo`) || title || 'Ward photo';
  // description carries the human context line: the note, plus what vision saw.
  const description = [cleanNote, u && u.summary && u.summary !== cleanNote ? `Looks like: ${u.summary}` : '']
    .filter(Boolean).join(' — ');
  const binary = await medplum.createBinary({ data: file, contentType: type, filename: `${label}.jpg` });
  const doc = {
    resourceType: 'DocumentReference',
    status: 'current',
    type: { coding: [{ code: 'clinical-photo', display: 'Clinical photograph' }], text: label },
    subject: { reference: `Patient/${patientId}` },
    // Encounter link is best-effort: listBedReports queries by patient, so the
    // photo shows in Reports either way; when we DO have the bed's encounter we
    // link it so the row carries visit context (shapeReportRow reads it via the
    // _include).
    ...(isUuid(encounterId) ? { context: { encounter: [{ reference: `Encounter/${encounterId}` }] } } : {}),
    date: new Date().toISOString(),
    ...(description ? { description } : {}),
    content: [{ attachment: { contentType: type, url: `Binary/${binary.id}`, title: label } }],
    // Photo context in the SAME _healther-bag extension convention as
    // medplum-photo.js / medplum-store.mjs — decoders recover the full bag,
    // pure-FHIR readers fall back to type/description above.
    extension: [{
      url: EXT_OPD_HEALTHER,
      valueString: JSON.stringify({
        kind: (u && u.kind) || 'other',
        category: 'clinical-photo',
        label,
        note: cleanNote || null,
        summary: (u && u.summary) || null,
        fields: (u && u.fields) || null,
        categorySource: u && u.source === 'vision' ? 'vision' : 'manual',
        source: 'ward-quickphoto',
        // Capture surface (owner ask 2026-07-10): where the photo was taken and
        // by whom, so the record carries the same context the user confirmed.
        surface: (context && context.surface) || null,
        capturedRole: (context && context.role) || null,
      }),
    }],
  };
  const created = await medplum.createResource(doc);
  return { ok: true, binaryId: binary.id, documentReferenceId: created.id };
}

/**
 * QuickPhoto — the capture modal. Props:
 *   { patientId (UUID), encounterId? (UUID), patientName?, onClose, onAttached?,
 *     context? ({ surface, role, patientName }) }
 * onAttached({ documentReferenceId }) fires after a successful write. `context`
 * pre-seeds the likely kind for the surface + a one-line confirmation sentence,
 * and is stamped into the DocumentReference bag (see attachWardPhoto).
 *
 * `uploader` — OPTIONAL write adapter. When omitted the modal writes through the
 * logged-in clinician's own RLS-scoped Medplum session (attachWardPhoto).
 * A surface that must keep its OWN write door (e.g. the OPD consult's
 * medplum-photo.js clinical-photo path) passes its own
 *   uploader({ file, patientId, encounterId, note, understanding, context }) → { documentReferenceId }
 * so the capture/context UX is shared but the write is NOT switched (AGENTS §2:
 * do not change write doors). The context (vision) layer is identical either way.
 */
export default function QuickPhoto({ patientId, encounterId, patientName, onClose, onAttached, context, uploader }) {
  const surface = context && context.surface;
  const seedKind = SURFACE_DEFAULT_KIND[surface] || 'other';
  const displayName = patientName || (context && context.patientName) || '';
  const [phase, setPhase] = React.useState('capture'); // capture | preview | done
  const [shot, setShot] = React.useState(null);         // { blob, dataUrl }
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  // Context layer: 'reading' | 'ready' | 'unavailable'; understanding = vision result.
  const [visionState, setVisionState] = React.useState('reading');
  const [visionMsg, setVisionMsg] = React.useState('');
  const [understanding, setUnderstanding] = React.useState(null);
  const [kind, setKind] = React.useState(seedKind);      // surface-seeded, vision-refined, user-editable
  const [camReady, setCamReady] = React.useState(false);
  const [camError, setCamError] = React.useState('');
  // Front/back lens (owner ask 2026-07-13): 'environment' (rear) ⇄ 'user' (front).
  // Seeded from the session memory so the modal reopens on the last-used lens. The
  // Flip control is shown ONLY when the device actually has >1 camera (see
  // hasMultiCam) — a one-camera laptop shows no dead button.
  const [facing, setFacing] = React.useState(LAST_FACING);
  const [hasMultiCam, setHasMultiCam] = React.useState(false);
  // Torch/flash (owner ask 2026-07-10): feature-detected honestly off the live video
  // track's capabilities; the toggle is hidden entirely when the device/browser does
  // not expose { torch } (desktop webcams, iOS Safari). Controlled via the standard
  // ImageCapture-era MediaStreamTrack.applyConstraints({ advanced:[{ torch }] }).
  const [torchSupported, setTorchSupported] = React.useState(false);
  const [torchOn, setTorchOn] = React.useState(false);
  // Spoken-note dictation (owner ask 2026-07-10): the shared window.OPDVoice hook —
  // the SAME Deepgram dictation the consult/quick-add use — fills the note field.
  const [dictating, setDictating] = React.useState(false);
  const [dictInterim, setDictInterim] = React.useState('');
  const [dictErr, setDictErr] = React.useState('');
  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const fileRef = React.useRef(null);
  const voiceRef = React.useRef(null);

  const secureCam = !!(typeof navigator !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  const voiceAvailable = typeof window !== 'undefined' && window.OPDVoice && typeof window.OPDVoice.create === 'function';

  const stopCam = React.useCallback(() => {
    try { streamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch (_) {}
    streamRef.current = null;
    setTorchSupported(false); setTorchOn(false);
  }, []);

  // Start the camera when in the capture phase, on the currently-chosen lens
  // (`facing` — rear 'environment' by default, front 'user' after a flip). Uses
  // `ideal` so a device that lacks the requested lens still opens its only camera
  // rather than failing. Re-runs whenever `facing` changes (the Flip button), so
  // there is ONE owner of the stream. On any failure we fall back to the file
  // input, not a dead screen.
  React.useEffect(() => {
    if (phase !== 'capture' || !secureCam) return undefined;
    let alive = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: facing } }, audio: false });
        if (!alive) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
        setCamReady(true);
        // Only expose the Flip control when the device genuinely has more than one
        // camera. enumerateDevices is called AFTER a granted getUserMedia so the
        // videoinput entries are reliably present (labels/ids populate post-grant).
        try {
          const devs = await navigator.mediaDevices.enumerateDevices();
          if (alive) setHasMultiCam(devs.filter((d) => d.kind === 'videoinput').length > 1);
        } catch (_) { /* enumerateDevices unsupported → no Flip UI */ }
        // Honest torch feature-detect: only rear cameras that report the { torch }
        // capability get the toggle; everything else keeps it hidden.
        try {
          const track = stream.getVideoTracks && stream.getVideoTracks()[0];
          const caps = track && track.getCapabilities ? track.getCapabilities() : null;
          if (caps && caps.torch === true) setTorchSupported(true);
        } catch (_) { /* getCapabilities unsupported → no torch UI */ }
      } catch (e) {
        setCamError(e && e.name === 'NotAllowedError' ? 'Camera permission denied — pick a photo instead.' : 'Camera unavailable — pick a photo instead.');
      }
    })();
    return () => { alive = false; stopCam(); setCamReady(false); };
  }, [phase, secureCam, stopCam, facing]);

  // Flip the lens (front ⇄ back) and remember it for the session. Changing
  // `facing` re-runs the camera-start effect above, which stops the old stream
  // (cleanup) and opens the new lens — we never touch the stream imperatively.
  function flipCamera() {
    setFacing((f) => { const next = f === 'user' ? 'environment' : 'user'; LAST_FACING = next; return next; });
  }

  React.useEffect(() => () => stopCam(), [stopCam]);

  // Stop any in-flight dictation on unmount so a closed modal never leaves the mic on.
  const stopDictation = React.useCallback(async ({ surfaceError = true } = {}) => {
    const s = voiceRef.current;
    voiceRef.current = null;
    setDictating(false); setDictInterim('');
    try {
      const result = await s?.stop();
      if (result?.transcriptionError) {
        if (surfaceError) setDictErr(`Mic error: ${result.transcriptionError.message || 'Speech transcription failed'}`);
        return false;
      }
      return true;
    } catch (error) {
      if (surfaceError) setDictErr(`Mic error: ${error?.message || 'Speech transcription failed'}`);
      return false;
    }
  }, []);
  React.useEffect(() => () => { stopDictation({ surfaceError: false }); }, [stopDictation]);

  // Toggle the camera torch/flash via the live track's constraints. Best-effort:
  // a device that advertised torch but rejects the constraint just no-ops.
  async function toggleTorch() {
    const track = streamRef.current && streamRef.current.getVideoTracks && streamRef.current.getVideoTracks()[0];
    if (!track) return;
    const next = !torchOn;
    try { await track.applyConstraints({ advanced: [{ torch: next }] }); setTorchOn(next); }
    catch (_) { setTorchSupported(false); /* device recanted torch support */ }
  }

  // Spoken note → note field, via the shared window.OPDVoice dictation hook. onFinal
  // mirrors each finalized phrase into the note (single source — we do NOT also apply
  // stop()'s transcript, which would double it, per the consult command-agent note).
  async function startDictation() {
    if (!voiceAvailable || voiceRef.current) return;
    setDictErr('');
    try {
      const session = window.OPDVoice.create({
        sessionType: 'opd-intake', speaker: 'Doctor',
        onInterim: (t) => setDictInterim(t || ''),
        onFinal: (t) => {
          const s = (t || '').trim();
          if (s) setNote((prev) => ((prev ? prev + ' ' : '') + s).slice(0, 140));
          setDictInterim('');
        },
        onError: (error) => setDictErr(`Mic error: ${error?.message || 'Speech transcription failed'}`),
      });
      voiceRef.current = session;
      const started = await session.start();
      if (started === false) {
        voiceRef.current = null;
        setDictating(false);
        return;
      }
      setDictating(true);
    } catch (e) {
      voiceRef.current = null; setDictating(false);
      setDictErr((e && e.message) ? `Mic error: ${e.message}` : 'Mic unavailable');
    }
  }
  const toggleDictation = () => { if (dictating) stopDictation(); else startDictation(); };

  // CONTEXT LAYER: when a shot enters preview, run understanding. Honest
  // degrade: on failure the slot shows WHY, the manual chips still work, and
  // attach is never blocked. A stale (superseded) result is dropped via token.
  const visionToken = React.useRef(0);
  React.useEffect(() => {
    if (phase !== 'preview' || !shot) return;
    const tok = ++visionToken.current;
    setVisionState('reading'); setVisionMsg(''); setUnderstanding(null);
    understandPhoto(shot.dataUrl).then((u) => {
      if (visionToken.current !== tok) return;
      setUnderstanding(u); setKind(u.kind); setVisionState('ready');
    }).catch((e) => {
      if (visionToken.current !== tok) return;
      setVisionMsg((e && e.message) || 'vision unavailable'); setVisionState('unavailable');
    });
  }, [phase, shot]);

  async function captureFrame() {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    try {
      const s = await toJpeg(v, v.videoWidth, v.videoHeight);
      stopCam(); setCamReady(false);
      setShot(s); setErr(''); setPhase('preview');
    } catch (e) { setErr((e && e.message) || 'Could not capture the photo.'); }
  }

  function onPickFile(e) {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    const img = new Image();
    img.onload = async () => {
      try {
        const s = await toJpeg(img, img.naturalWidth, img.naturalHeight);
        URL.revokeObjectURL(img.src);
        setShot(s); setErr(''); setPhase('preview');
      } catch (err2) { setErr((err2 && err2.message) || 'Could not read that image.'); }
    };
    img.onerror = () => setErr('Could not read that image.');
    img.src = URL.createObjectURL(f);
  }

  function retake() {
    visionToken.current += 1; // drop any in-flight understanding
    stopDictation();
    setShot(null); setErr(''); setUnderstanding(null); setKind(seedKind); setVisionState('reading'); setPhase('capture');
  }

  async function attach() {
    if (busy || !shot) return;
    setBusy(true); setErr('');
    try {
      const file = new File([shot.blob], 'ward-photo.jpg', { type: 'image/jpeg' });
      // The user-edited kind wins over what vision proposed; extracted fields
      // ride along only when vision actually read them.
      const u = { kind, summary: (understanding && understanding.summary) || '', fields: (understanding && understanding.fields) || {}, source: understanding ? 'vision' : 'manual' };
      // Default write door = the ward device; a surface may inject its own
      // (uploader) to keep its existing write path (do not switch doors).
      const write = uploader || attachWardPhoto;
      const res = await write({ file, patientId, encounterId, note, understanding: u, context });
      setPhase('done');
      onAttached?.({ documentReferenceId: res.documentReferenceId });
      setTimeout(() => onClose?.(), 1100);
    } catch (e) {
      // Fail loud: keep the preview + note so the user can retry; nothing is lost.
      setErr((e && e.message) || 'Upload failed — the photo was not saved. Try again.');
    } finally { setBusy(false); }
  }

  return createPortal(
    <div
      role="dialog" aria-modal="true" aria-label="Attach a photo" data-shell="v2"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 9600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(0,0,0,.55)' }}>
      <div
        className="bg-card text-card-foreground"
        style={{ width: '100%', maxWidth: 400, borderRadius: 14, border: '1px solid hsl(var(--border))', boxShadow: '0 20px 60px -12px rgba(0,0,0,.5)', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '92vh' }}>
        {/* Header */}
        <div className="border-b border-border" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px' }}>
          <Camera className="size-4 text-primary" />
          <span className="text-[13px] font-semibold text-foreground" style={{ flex: 1, minWidth: 0 }}>
            Attach photo{patientName ? ` · ${patientName}` : ''}
          </span>
          <Button type="button" variant="ghost" size="icon" aria-label="Close" className="text-muted-foreground" onClick={() => !busy && onClose?.()}>
            <X className="size-4" />
          </Button>
        </div>

        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0, overflowY: 'auto' }}>
          {phase === 'capture' && (
            <>
              <div style={{ position: 'relative', width: '100%', aspectRatio: '3 / 4', borderRadius: 10, overflow: 'hidden', background: 'hsl(var(--muted))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {secureCam && !camError
                  ? <video ref={videoRef} playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <div className="text-muted-foreground" style={{ textAlign: 'center', padding: 20, fontSize: 12.5 }}>
                      <ImageIcon className="size-7" style={{ margin: '0 auto 8px', opacity: .6 }} />
                      {camError || 'No camera here — pick a photo from the device.'}
                    </div>}
                {/* Flip lens (front ⇄ back) — shown only when the device has >1
                    camera, so a single-camera laptop shows no dead button. Press
                    feedback only (≤120ms transform), per ANIMATION_STANDARDS. */}
                {secureCam && !camError && hasMultiCam && (
                  <button
                    type="button" onClick={flipCamera} disabled={!camReady}
                    aria-label={facing === 'user' ? 'Switch to back camera' : 'Switch to front camera'}
                    title="Flip camera"
                    className="transition-transform duration-100 ease-out active:scale-[0.97]"
                    style={{
                      position: 'absolute', top: 10, left: 10, width: 38, height: 38, borderRadius: 999,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      cursor: camReady ? 'pointer' : 'default', opacity: camReady ? 1 : 0.5,
                      border: '1px solid rgba(255,255,255,.4)', background: 'rgba(0,0,0,.45)',
                      color: '#fff', backdropFilter: 'blur(2px)',
                    }}>
                    <SwitchCamera className="size-[18px]" />
                  </button>
                )}
                {/* Torch/flash toggle — only when the live track exposes { torch }. */}
                {torchSupported && (
                  <button
                    type="button" onClick={toggleTorch} aria-pressed={torchOn}
                    aria-label={torchOn ? 'Turn flash off' : 'Turn flash on'} title={torchOn ? 'Flash on' : 'Flash off'}
                    style={{
                      position: 'absolute', top: 10, right: 10, width: 38, height: 38, borderRadius: 999,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                      border: torchOn ? '1px solid #F5C518' : '1px solid rgba(255,255,255,.4)',
                      background: torchOn ? 'rgba(245,197,24,.92)' : 'rgba(0,0,0,.45)',
                      color: torchOn ? '#1a1a00' : '#fff', backdropFilter: 'blur(2px)',
                    }}>
                    <Flashlight className="size-[18px]" />
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {secureCam && !camError && (
                  <Button type="button" className="flex-1" style={{ height: 44 }} disabled={!camReady} onClick={captureFrame}>
                    <Camera className="size-4" /> Capture
                  </Button>
                )}
                <Button type="button" variant="outline" className={secureCam && !camError ? '' : 'flex-1'} style={{ height: 44 }} onClick={() => fileRef.current?.click()}>
                  <ImageIcon className="size-4" /> {secureCam && !camError ? 'Gallery' : 'Pick a photo'}
                </Button>
              </div>
              {/* Gallery / system picker — deliberately NO `capture` attribute so
                  the OS file chooser opens (photo library on tablet/phone, file
                  picker on desktop) instead of jumping straight into the camera.
                  The live viewfinder + Capture button above is the take-a-photo
                  path; this button is the choose-from-gallery path. */}
              <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} style={{ display: 'none' }} />
            </>
          )}

          {phase === 'preview' && shot && (
            <>
              <div style={{ width: '100%', aspectRatio: '3 / 4', borderRadius: 10, overflow: 'hidden', background: 'hsl(var(--muted))' }}>
                <img src={shot.dataUrl} alt="Captured preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>

              {/* CONTEXT LAYER — the extraction slot: what the vision leg saw,
                  or an honest unavailable state. Never blocks attach. */}
              <div className="rounded-[9px] border border-border bg-muted/40" style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {visionState === 'reading' && (
                  <span className="text-[11.5px] text-muted-foreground">Reading photo…</span>
                )}
                {visionState === 'ready' && understanding && (
                  <>
                    <span className="text-[12px] font-semibold text-foreground">Looks like: {understanding.summary || kindLabel(understanding.kind)}</span>
                    {fieldLines(understanding.fields).map((l) => (
                      <span key={l} className="text-[11.5px] text-muted-foreground">{l}</span>
                    ))}
                  </>
                )}
                {visionState === 'unavailable' && (
                  <span className="text-[11.5px] text-muted-foreground" role="status">
                    {visionMsg || 'Vision unavailable'} — pick the type below.
                  </span>
                )}
              </div>

              {/* Type chips — pre-selected by vision, always user-editable */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {PHOTO_KINDS.map((k) => (
                  <Button
                    key={k.key} type="button" size="sm"
                    variant={kind === k.key ? 'default' : 'outline'}
                    aria-pressed={kind === k.key}
                    style={{ height: 32, borderRadius: 999 }}
                    onClick={() => setKind(k.key)}>
                    {k.label}
                  </Button>
                ))}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label htmlFor="qp-note" className="text-[11px] font-semibold text-muted-foreground" style={{ flex: 1, minWidth: 0 }}>Note (optional)</label>
                  {/* MIC pill — spoken note via the shared dictation hook; hidden when
                      the OPDVoice hook is not on the page. */}
                  {voiceAvailable && (
                    <button
                      type="button" onClick={toggleDictation} aria-pressed={dictating}
                      aria-label={dictating ? 'Stop dictation' : 'Dictate a note'}
                      className={dictating
                        ? 'border-transparent bg-destructive text-destructive-foreground'
                        : 'border-border bg-background text-muted-foreground hover:text-foreground'}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5, height: 26, padding: '0 10px',
                        borderRadius: 999, borderWidth: 1, borderStyle: 'solid', fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
                      }}>
                      <Mic className="size-3.5" />
                      {dictating ? 'Listening…' : 'Speak'}
                    </button>
                  )}
                </div>
                <input
                  id="qp-note"
                  type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={140}
                  placeholder="e.g. wound site, left forearm"
                  className="bg-background text-foreground"
                  style={{ height: 40, borderRadius: 8, border: '1px solid hsl(var(--border))', padding: '0 10px', fontSize: 13, fontWeight: 400 }} />
                {dictating && dictInterim && (
                  <span className="text-[11px] text-muted-foreground" style={{ fontStyle: 'italic' }}>{dictInterim}</span>
                )}
                {dictErr && <span className="text-[11px] text-destructive">{dictErr}</span>}
              </div>
              {/* CONTEXT-AWARE CONFIRMATION — one line that states where/who/what
                  so the user confirms ONCE already understanding the write. Only
                  shown when a capture surface is supplied. */}
              {surface && (
                <div
                  className="rounded-[9px] border text-foreground"
                  role="note"
                  style={{ padding: '9px 11px', fontSize: 12.5, lineHeight: 1.4, background: 'hsl(var(--ai-bg))', borderColor: 'hsl(var(--ai-border))', color: 'hsl(var(--ai-foreground))' }}>
                  {contextSentence({ surface, role: context && context.role, name: displayName, kind })}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <Button type="button" variant="outline" style={{ height: 44 }} disabled={busy} onClick={retake}>
                  <RotateCcw className="size-4" /> Retake
                </Button>
                <Button type="button" className="flex-1" style={{ height: 44 }} disabled={busy} onClick={attach}>
                  {busy ? 'Attaching…' : <><Check className="size-4" /> Attach</>}
                </Button>
              </div>
            </>
          )}

          {phase === 'done' && (
            <div className="text-foreground" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '24px 0' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: '50%', background: 'hsl(var(--emerald-bg, var(--muted)))', color: 'hsl(var(--emerald-fg, var(--primary)))' }}>
                <Check className="size-5" />
              </span>
              <span className="text-[13px] font-semibold">Photo attached</span>
              <span className="text-[11.5px] text-muted-foreground">It’s now in this patient’s Reports.</span>
            </div>
          )}

          {err && <div className="text-[11.5px] leading-relaxed text-destructive" role="alert">{err}</div>}
        </div>
      </div>
    </div>,
    document.body,
  );
}
